import {
    ALL_SUBCATEGORIES_SORTED,
    NICHE_MAIN_ALPHABETICAL,
    NICHE_MAIN_ORDER,
    NICHE_SUBS_BY_MAIN,
    findMainCategoryForSub as findMainStatic,
    getSubcategoriesForMain as getSubsStatic,
    isNicheMainCategory as isMainStatic,
    isValidSubForMain as isValidSubStatic,
} from '../pages/BrandDashboard/ugcCampaignCategorySchema';

export interface IContentSubcategory {
    _id?: string;
    name: string;
    slug?: string;
    sort_order?: number;
}

export interface IContentCategory {
    _id?: string;
    name: string;
    slug?: string;
    icon_key?: string | null;
    sort_order?: number;
    popular?: boolean;
    active?: boolean;
    subcategories: IContentSubcategory[];
}

export interface IContentCategoryCatalog {
    mains: string[];
    mainsAlphabetical: string[];
    popular: string[];
    subsByMain: Record<string, string[]>;
    categories: IContentCategory[];
    source: 'api' | 'fallback';
}

export function buildCatalogFromCategories(
    categories: IContentCategory[],
    source: 'api' | 'fallback',
): IContentCategoryCatalog {
    const sorted = [...categories].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name),
    );
    const mains = sorted.map(c => c.name);
    const popular = sorted.filter(c => c.popular).map(c => c.name);
    const subsByMain: Record<string, string[]> = {};
    for (const cat of sorted) {
        subsByMain[cat.name] = [...(cat.subcategories || [])]
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name))
            .map(s => s.name);
    }
    return {
        mains,
        mainsAlphabetical: [...mains].sort((a, b) => a.localeCompare(b)),
        popular: popular.length ? popular : mainsAlphabeticalSlice(mains, 5),
        subsByMain,
        categories: sorted,
        source,
    };
}

function mainsAlphabeticalSlice(mains: string[], n: number) {
    return [...mains].sort((a, b) => a.localeCompare(b)).slice(0, n);
}

export function fallbackContentCategoryCatalog(): IContentCategoryCatalog {
    const categories: IContentCategory[] = NICHE_MAIN_ORDER.map((name, i) => ({
        name,
        sort_order: i,
        popular: NICHE_MAIN_ALPHABETICAL.slice(0, 5).includes(name as any),
        subcategories: (NICHE_SUBS_BY_MAIN[name] || []).map((sub, j) => ({
            name: sub,
            sort_order: j,
        })),
    }));
    // Match screenshot popular set when using fallback
    const popularSet = new Set([
        'Art & Design',
        'Automotive',
        'Beauty & Skincare',
        'Book Recommendations',
        'Business & Entrepreneurship',
    ]);
    for (const c of categories) c.popular = popularSet.has(c.name);
    return buildCatalogFromCategories(categories, 'fallback');
}

let cached: IContentCategoryCatalog | null = null;

export function getRawContentCategoriesCache(): IContentCategoryCatalog | null {
    return cached;
}

export function setContentCategoriesCache(catalog: IContentCategoryCatalog) {
    cached = catalog;
}

export function getCachedContentCategories(): IContentCategoryCatalog {
    return cached || fallbackContentCategoryCatalog();
}

export function clearContentCategoriesCache() {
    cached = null;
    try {
        // Lazy require avoids store ↔ saga ↔ util circular import at bootstrap.
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        const { store } = require('../redux/store');
        store.dispatch({ type: 'contentCategories/CLEAR' });
    } catch {
        // store unavailable (e.g. isolated tests)
    }
}

export function getSubcategoriesForMainFromCatalog(catalog: IContentCategoryCatalog, main: string): string[] {
    return [...(catalog.subsByMain[main] || [])].sort((a, b) => a.localeCompare(b));
}

export function isValidSubForMainFromCatalog(catalog: IContentCategoryCatalog, main: string, sub: string): boolean {
    return (catalog.subsByMain[main] || []).includes(sub.trim());
}

export function findMainCategoryForSubFromCatalog(catalog: IContentCategoryCatalog, sub: string): string | null {
    const normalized = sub.trim();
    if (!normalized) return null;
    for (const main of catalog.mains) {
        if ((catalog.subsByMain[main] || []).includes(normalized)) return main;
    }
    return findMainStatic(normalized);
}

/** Compatibility helpers using cache/fallback. */
export function getSubcategoriesForMain(main: string): string[] {
    const catalog = getCachedContentCategories();
    if (catalog.source === 'api') return getSubcategoriesForMainFromCatalog(catalog, main);
    return getSubsStatic(main);
}

export function isValidSubForMain(main: string, sub: string): boolean {
    const catalog = getCachedContentCategories();
    if (catalog.source === 'api') return isValidSubForMainFromCatalog(catalog, main, sub);
    return isValidSubStatic(main, sub);
}

export function findMainCategoryForSub(sub: string): string | null {
    return findMainCategoryForSubFromCatalog(getCachedContentCategories(), sub);
}

export function isNicheMainCategory(value: string): boolean {
    const catalog = getCachedContentCategories();
    if (catalog.source === 'api') return catalog.mains.includes(value);
    return isMainStatic(value);
}

export { ALL_SUBCATEGORIES_SORTED, NICHE_MAIN_ORDER, NICHE_MAIN_ALPHABETICAL };
